<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    type CustomerUser as User,
    type TActionAutoCommitSettings,
    AiSettingsService
  } from '@/types/generated'
  import Switch from '@/components/ui/switch/Switch.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { genericErrorToast, genericSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    MagnifyingGlassIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CogIcon
  } from '@heroicons/vue/24/outline'
  import { debounce } from '@/lib/timing'

  // Types for available actions response
  interface Translation {
    de?: string
    en?: string
  }

  interface AutoCommitSettingsParam {
    key: string
    name: Translation
    description: Translation
    help_text: Translation
    can_auto_commit: boolean
    default: boolean
    additional_user_instructions: string
    confidence_threshold: number
  }

  interface AutoCommitSettingsResponse {
    params: AutoCommitSettingsParam[]
  }

  // Merged setting type for frontend display
  interface MergedAutoCommitSetting {
    key: string
    name: string
    description: string
    help_text: string
    can_auto_commit: boolean
    default: boolean
    additional_user_instructions: string
    confidence_threshold: number
    is_organization_setting: boolean // Whether this comes from org settings
    uuid?: string // UUID if it's an existing org setting
  }

  // Translation setup
  const { t } = useTranslation({
    de: {
      title: 'KI‑Autonomie',
      intro: 'Konfigurieren Sie hier, welche KI‑Aktionen die KI eigenständig ausführen darf.',
      groupTitle: 'Autonomie-Einstellungen',
      groupIntro: 'Bestimmen Sie für jede KI‑Aktion, ob sie automatisch ausgeführt werden darf.',
      loading: 'Lade Einstellungen...',
      error: 'Fehler beim Laden der KI-Einstellungen',
      saveError: 'Fehler beim Speichern der Einstellungen',
      enableAutoCommit: 'Autonome Ausführung aktivieren',
      confidenceThreshold: 'Vertrauensschwelle (%)',
      additionalInstructions: 'Zusätzliche Anweisungen',
      additionalInstructionsPlaceholder:
        'Geben Sie hier zusätzliche Anweisungen für diese Aktion ein...',
      noSettings: 'Keine Autonomie-Einstellungen verfügbar',
      searchPlaceholder: 'Aktionen durchsuchen …',
      colAction: 'Aktion',
      colAutoCommit: 'Autonomie',
      colConfidence: 'Vertrauen (%)',
      expandRow: 'Zeile erweitern',
      collapseRow: 'Zeile einklappen',
      confidenceDesc: 'Mindestvertrauen für autonome Ausführung',
      instructionsDesc: 'Zusätzliche Anweisungen für diese Aktion'
    },
    en: {
      title: 'AI autonomy',
      intro: 'Configure which AI actions the AI is allowed to execute autonomously.',
      groupTitle: 'Autonomy settings',
      groupIntro: 'Decide for each AI action whether it can be executed automatically.',
      loading: 'Loading settings...',
      error: 'Error loading AI settings',
      saveError: 'Error saving settings',
      enableAutoCommit: 'Enable autonomous execution',
      confidenceThreshold: 'Confidence Threshold (%)',
      additionalInstructions: 'Additional instructions',
      additionalInstructionsPlaceholder: 'Enter additional instructions for this action...',
      noSettings: 'No autonomy settings available',
      searchPlaceholder: 'Search actions …',
      colAction: 'Action',
      colAutoCommit: 'Autonomy',
      colConfidence: 'Confidence (%)',
      expandRow: 'Expand row',
      collapseRow: 'Collapse row',
      confidenceDesc: 'Minimum confidence for autonomous execution',
      instructionsDesc: 'Additional instructions for this action'
    },
    fr: {
      title: 'Autonomie de l’IA',
      intro: 'Configurez les actions que l’IA est autorisée à exécuter de manière autonome.',
      groupTitle: 'Paramètres d’autonomie',
      groupIntro: 'Décidez pour chaque action de l’IA si elle peut être exécutée automatiquement.',
      loading: 'Chargement des paramètres…',
      error: 'Erreur lors du chargement des paramètres de l’IA',
      saveError: 'Erreur lors de l’enregistrement des paramètres',
      enableAutoCommit: 'Activer l’exécution autonome',
      confidenceThreshold: 'Seuil de confiance (%)',
      additionalInstructions: 'Instructions supplémentaires',
      additionalInstructionsPlaceholder:
        'Saisir des instructions supplémentaires pour cette action…',
      noSettings: 'Aucun paramètre d’autonomie disponible',
      searchPlaceholder: 'Rechercher des actions…',
      colAction: 'Action',
      colAutoCommit: 'Autonomie',
      colConfidence: 'Confiance (%)',
      expandRow: 'Développer la ligne',
      collapseRow: 'Réduire la ligne',
      confidenceDesc: 'Confiance minimale pour une exécution autonome',
      instructionsDesc: 'Instructions supplémentaires pour cette action'
    }
  })

  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user)

  // State
  const existingAutoCommitSettings = ref<TActionAutoCommitSettings[]>([])
  const availableAutoCommitActions = ref<AutoCommitSettingsResponse | null>(null)
  const mergedAutoCommitSettings = ref<MergedAutoCommitSetting[]>([])
  const isLoading = ref(false)
  const isSaving = ref(false)
  const searchQuery = ref('')
  const expandedRows = ref<Set<string>>(new Set())

  // Form data for auto commit settings
  const autoCommitFormData = ref<
    Record<string, { enabled: boolean; instructions: string; confidence_threshold: number }>
  >({})

  // Computed properties
  const filteredAutoCommitSettings = computed(() => {
    const q = searchQuery.value.trim().toLowerCase()
    if (!q) return mergedAutoCommitSettings.value
    return mergedAutoCommitSettings.value.filter((setting) => {
      const name = setting.name.toLowerCase()
      const desc = setting.description.toLowerCase()
      const helpText = setting.help_text.toLowerCase()
      return (
        name.includes(q) ||
        desc.includes(q) ||
        helpText.includes(q) ||
        setting.key.toLowerCase().includes(q)
      )
    })
  })

  // Row expansion functions
  const toggleRowExpansion = (settingKey: string) => {
    const newExpanded = new Set(expandedRows.value)
    if (newExpanded.has(settingKey)) {
      newExpanded.delete(settingKey)
    } else {
      newExpanded.add(settingKey)
    }
    expandedRows.value = newExpanded
  }

  const isRowExpanded = (settingKey: string) => expandedRows.value.has(settingKey)

  onMounted(async () => {
    const fetching = []
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)

    // Load auto commit settings
    await loadAutoCommitSettings()
  })

  const loadAutoCommitSettings = async () => {
    isLoading.value = true

    try {
      await Promise.all([fetchExistingAutoCommitSettings(), fetchAvailableAutoCommitActions()])
      mergeAutoCommitSettings()
    } catch (error) {
      console.error('Error loading auto commit settings:', error)
      genericErrorToast(t('error'))
    } finally {
      isLoading.value = false
    }
  }

  const fetchExistingAutoCommitSettings = async () => {
    try {
      // Get AI settings which now include auto commit settings inline
      const response = await AiSettingsService.aiSettingsList()
      // Extract auto commit settings from the first AI settings object
      const aiSettings = response.results?.[0]
      existingAutoCommitSettings.value = aiSettings?.auto_commit_settings || []
    } catch (error) {
      console.error('Error fetching existing settings:', error)
      genericErrorToast(t('error'))
    }
  }

  const fetchAvailableAutoCommitActions = async () => {
    try {
      const response = await AiSettingsService.aiSettingsAvailableActionsRetrieve()
      availableAutoCommitActions.value = response as AutoCommitSettingsResponse
    } catch (error) {
      console.error('Error fetching available actions:', error)
      // Don't show error toast for this as it's expected to fail when no actions are available
      availableAutoCommitActions.value = { params: [] }
    }
  }

  const getTranslatedText = (translation: Translation | undefined, fallback: string): string => {
    if (!translation) return fallback
    return translation.de || translation.en || fallback
  }

  const mergeAutoCommitSettings = () => {
    if (!availableAutoCommitActions.value) {
      return
    }

    const existingSettingsMap = new Map<string, TActionAutoCommitSettings>()

    // Create a map of existing settings by key
    for (const setting of existingAutoCommitSettings.value) {
      existingSettingsMap.set(setting.auto_commit_settings_key, setting)
    }

    const merged: MergedAutoCommitSetting[] = []

    // Process available actions from the system
    for (const param of availableAutoCommitActions.value.params) {
      const existingSetting = existingSettingsMap.get(param.key)

      merged.push({
        key: param.key,
        name: getTranslatedText(param.name, param.key),
        description: getTranslatedText(param.description, ''),
        help_text: getTranslatedText(param.help_text, ''),
        can_auto_commit: existingSetting?.can_auto_commit ?? param.can_auto_commit,
        default: param.default,
        additional_user_instructions:
          existingSetting?.additional_user_instructions ?? param.additional_user_instructions,
        confidence_threshold: existingSetting?.confidence_threshold ?? param.confidence_threshold,
        is_organization_setting: !!existingSetting,
        uuid: existingSetting?.uuid
      })

      // Remove from map so we don't process it again
      existingSettingsMap.delete(param.key)
    }

    // Add any organization-specific settings that don't have system defaults
    for (const [key, existingSetting] of existingSettingsMap) {
      merged.push({
        key: existingSetting.auto_commit_settings_key,
        name: existingSetting.auto_commit_settings_key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        description: `Organization-specific setting for ${existingSetting.auto_commit_settings_key}`,
        help_text: 'This setting is specific to your organization.',
        can_auto_commit: existingSetting.can_auto_commit ?? false,
        default: false,
        additional_user_instructions: existingSetting.additional_user_instructions ?? '',
        confidence_threshold: existingSetting.confidence_threshold ?? 70,
        is_organization_setting: true,
        uuid: existingSetting.uuid
      })
    }

    mergedAutoCommitSettings.value = merged
    initializeAutoCommitFormData()
  }

  const initializeAutoCommitFormData = () => {
    const formData: Record<
      string,
      { enabled: boolean; instructions: string; confidence_threshold: number }
    > = {}

    for (const setting of mergedAutoCommitSettings.value) {
      formData[setting.key] = {
        enabled: setting.can_auto_commit,
        instructions: setting.additional_user_instructions,
        confidence_threshold: setting.confidence_threshold
      }
    }

    autoCommitFormData.value = formData
  }

  const handleAutoCommitFormSave = async () => {
    if (isSaving.value) return

    try {
      isSaving.value = true

      // Get the current AI settings
      const aiSettingsResponse = await AiSettingsService.aiSettingsList()
      const currentAiSettings = aiSettingsResponse.results?.[0]

      if (!currentAiSettings) {
        console.error('No AI settings found')
        genericErrorToast(t('saveError'))
        return
      }

      // Build the updated auto commit settings array
      const updatedAutoCommitSettings = []

      for (const setting of mergedAutoCommitSettings.value) {
        const formData = autoCommitFormData.value[setting.key]
        if (!formData) continue

        updatedAutoCommitSettings.push({
          uuid: setting.uuid,
          auto_commit_settings_key: setting.key,
          can_auto_commit: formData.enabled,
          additional_user_instructions: formData.instructions,
          confidence_threshold: formData.confidence_threshold
        })
      }

      // Update the AI settings with the new auto commit settings
      await AiSettingsService.aiSettingsPartialUpdate({
        uuid: currentAiSettings.uuid,
        requestBody: {
          auto_commit_settings: updatedAutoCommitSettings
        }
      })

      // Refresh the settings by fetching them again
      await fetchExistingAutoCommitSettings()
      mergeAutoCommitSettings()

      genericSuccessToast('Auto commit settings saved successfully')
    } catch (error) {
      console.error('Error updating auto commit settings:', error)
      genericErrorToast(t('saveError'))
    } finally {
      isSaving.value = false
    }
  }

  const AUTO_SAVE_DEBOUNCE_MS = 1000
  const debouncedSave = debounce(handleAutoCommitFormSave, AUTO_SAVE_DEBOUNCE_MS)

  const updateAutoCommitFormData = (
    key: string,
    field: 'enabled' | 'instructions' | 'confidence_threshold',
    value: boolean | string | number
  ) => {
    if (!autoCommitFormData.value[key]) {
      autoCommitFormData.value[key] = { enabled: false, instructions: '', confidence_threshold: 70 }
    }
    if (field === 'enabled') {
      autoCommitFormData.value[key][field] = value as boolean
    } else if (field === 'confidence_threshold') {
      autoCommitFormData.value[key][field] = value as number
    } else {
      autoCommitFormData.value[key][field] = value as string
    }
    debouncedSave()
  }
</script>

<template>
  <SettingsPage v-if="user">
    <template #actions></template>

    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8"
    >
      <div class="text-gray-500">{{ t('loading') }}</div>
    </div>

    <div
      v-else
      class="space-y-6"
    >
      <div class="space-y-2">
        <h2 class="text-lg font-semibold text-gray-900">{{ t('title') }}</h2>
        <p class="text-sm text-gray-600">
          {{ t('intro') }}
        </p>
      </div>

      <div class="w-full max-w-6xl space-y-4">
        <h3 class="text-md flex items-center gap-2 font-medium text-gray-900">
          <CogIcon class="size-5 text-purple-600" />
          <span>{{ t('groupTitle') }}</span>
        </h3>
        <p class="text-sm text-gray-600">{{ t('groupIntro') }}</p>

        <div class="relative">
          <input
            v-model="searchQuery"
            :placeholder="t('searchPlaceholder')"
            class="w-full rounded-lg border border-gray-200 bg-white py-2.5 pe-3 ps-10 text-sm shadow-sm
              focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
            type="text"
          />
          <MagnifyingGlassIcon
            class="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400"
          />
        </div>

        <div
          v-if="filteredAutoCommitSettings.length === 0"
          class="text-sm text-gray-500"
        >
          {{ t('noSettings') }}
        </div>

        <div v-else-if="filteredAutoCommitSettings.length">
          <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div
              class="hidden grid-cols-12 items-center bg-gray-50 px-4 py-3 text-xs font-medium text-gray-600 md:grid"
            >
              <div class="col-span-6">{{ t('colAction') }}</div>
              <div class="col-span-2 flex items-center justify-center">
                <span>{{ t('colAutoCommit') }}</span>
              </div>
              <div class="col-span-3 flex items-center justify-center">
                <span>{{ t('colConfidence') }}</span>
              </div>
              <div class="col-span-1 flex items-center justify-center">
                <span></span>
              </div>
            </div>

            <div class="divide-y divide-gray-200">
              <div
                v-for="setting in filteredAutoCommitSettings"
                :key="setting.key"
                class="divide-y divide-gray-100"
              >
                <!-- Main row -->
                <div class="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-12 md:gap-2">
                  <div class="md:col-span-6">
                    <div class="flex items-center gap-2">
                      <h4 class="text-sm font-medium text-gray-900">{{ setting.name }}</h4>
                      <span
                        v-if="setting.is_organization_setting"
                        class="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800"
                      >
                        Custom
                      </span>
                    </div>
                    <p class="text-xs text-gray-500">{{ setting.description }}</p>
                    <p
                      v-if="setting.help_text"
                      class="text-xs text-gray-400"
                    >
                      {{ setting.help_text }}
                    </p>
                  </div>

                  <div class="flex items-center justify-between md:col-span-2 md:justify-center">
                    <Switch
                      :checked="autoCommitFormData[setting.key]?.enabled ?? setting.can_auto_commit"
                      :disabled="isSaving"
                      class="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-gray-200"
                      @update:checked="
                        (value) => updateAutoCommitFormData(setting.key, 'enabled', value)
                      "
                    />
                  </div>

                  <div class="flex items-center justify-between md:col-span-3 md:justify-center">
                    <div class="w-20">
                      <input
                        :value="
                          autoCommitFormData[setting.key]?.confidence_threshold ??
                          setting.confidence_threshold
                        "
                        :disabled="
                          isSaving ||
                          !(autoCommitFormData[setting.key]?.enabled ?? setting.can_auto_commit)
                        "
                        type="number"
                        min="0"
                        max="100"
                        class="w-full rounded border border-gray-200 px-2 py-1 text-center text-sm focus:border-purple-500
                          focus:outline-none focus:ring-1 focus:ring-purple-100 disabled:bg-gray-50 disabled:text-gray-400"
                        @input="
                          (e) =>
                            updateAutoCommitFormData(
                              setting.key,
                              'confidence_threshold',
                              Number((e.target as HTMLInputElement).value)
                            )
                        "
                      />
                    </div>
                  </div>

                  <div class="flex items-center justify-between md:col-span-1 md:justify-center">
                    <button
                      v-if="autoCommitFormData[setting.key]?.enabled ?? setting.can_auto_commit"
                      type="button"
                      :title="isRowExpanded(setting.key) ? t('collapseRow') : t('expandRow')"
                      class="rounded text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500
                        focus:ring-offset-1"
                      @click="toggleRowExpansion(setting.key)"
                    >
                      <ChevronDownIcon
                        v-if="isRowExpanded(setting.key)"
                        class="size-4"
                      />
                      <ChevronRightIcon
                        v-else
                        class="size-4"
                      />
                    </button>
                  </div>
                </div>

                <!-- Expanded row for additional instructions -->
                <div
                  v-if="
                    isRowExpanded(setting.key) &&
                    (autoCommitFormData[setting.key]?.enabled ?? setting.can_auto_commit)
                  "
                  class="bg-gray-50 px-4 py-4"
                >
                  <div class="space-y-2">
                    <label class="text-sm font-medium text-gray-700">
                      {{ t('additionalInstructions') }}
                    </label>
                    <p class="text-xs text-gray-500">{{ t('instructionsDesc') }}</p>
                    <textarea
                      :value="
                        autoCommitFormData[setting.key]?.instructions ??
                        setting.additional_user_instructions
                      "
                      :placeholder="t('additionalInstructionsPlaceholder')"
                      :disabled="isSaving"
                      rows="3"
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-purple-500
                        focus:outline-none focus:ring-2 focus:ring-purple-100 disabled:bg-gray-50 disabled:text-gray-400"
                      @input="
                        (e) =>
                          updateAutoCommitFormData(
                            setting.key,
                            'instructions',
                            (e.target as HTMLTextAreaElement).value
                          )
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>
